/**
 * Um dos textos legais (docs/spec/termos.md), desenhado a partir do Markdown: na página pública
 * (`variant="page"`, com o título e o sumário) e nos diálogos do cadastro e do novo aceite
 * (`variant="dialog"`, em que o título é o do diálogo). Os links para o outro texto chamam
 * `onNavigate`, para o diálogo trocar de aba e a página trocar de endereço sem recarregar.
 */
import { Fragment, useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { translate, type Language } from '../i18n/index.js';
import { CURRENT_TOS_VERSION, TOS_UPDATED_AT } from '../shared.js';
import { legalRoute, loadLegal, type LegalDoc } from './documents.js';
import { parseMarkdown, type Block, type Inline } from './markdown.js';
import './legal.css';

/** A data da versão por extenso: "3 de novembro de 2026", "November 3, 2026". */
export function legalDate(language: Language): string {
  return new Date(`${TOS_UPDATED_AT}T12:00:00Z`).toLocaleDateString(language, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function LegalDocument({
  doc,
  language,
  variant,
  onNavigate,
}: {
  doc: LegalDoc;
  language: Language;
  variant: 'page' | 'dialog';
  onNavigate?: (doc: LegalDoc) => void;
}) {
  const key = `${doc}.${language}`;
  const [loaded, setLoaded] = useState<{ key: string; blocks: Block[] | null } | null>(null);
  useEffect(() => {
    let alive = true;
    loadLegal(doc, language).then(
      (text) => alive && setLoaded({ key, blocks: parseMarkdown(text) }),
      () => alive && setLoaded({ key, blocks: null }),
    );
    return () => {
      alive = false;
    };
  }, [doc, language, key]);

  const say = (k: string, params?: Record<string, string | number>) => translate(language, k, params);
  if (loaded?.key !== key) return <p className="rd-legal__status">{say('legal.loading')}</p>;
  if (!loaded.blocks) return <p className="rd-legal__status">{say('legal.error')}</p>;

  const blocks = loaded.blocks;
  const sections = blocks.filter((b): b is Extract<Block, { kind: 'heading' }> => b.kind === 'heading' && b.level === 2);
  const meta = <p className="rd-legal__meta">{say('legal.meta', { version: CURRENT_TOS_VERSION, date: legalDate(language) })}</p>;
  const link = (href: string, children: ReactNode, i: number) => <LegalLink key={i} href={href} onNavigate={onNavigate}>{children}</LegalLink>;

  return (
    <article className={`rd-legal__doc rd-legal__doc--${variant}`} lang={language}>
      {blocks.map((block, i) => {
        if (block.kind === 'heading') {
          if (block.level === 1) {
            return variant === 'page' ? (
              <Fragment key={i}>
                <h1>{block.text}</h1>
                {meta}
                <nav className="rd-legal__toc" aria-label={say('legal.toc')}>
                  <p>{say('legal.toc')}</p>
                  <ol>
                    {sections.map((s) => (
                      <li key={s.id}>
                        <a href={`#legal-${s.id}`} onClick={jump(`legal-${s.id}`)}>
                          {s.text.replace(/^\d+\.\s*/, '')}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              </Fragment>
            ) : (
              <Fragment key={i}>{meta}</Fragment>
            );
          }
          return block.level === 2 ? (
            <h2 key={i} id={`legal-${block.id}`}>
              {block.text}
            </h2>
          ) : (
            <h3 key={i}>{block.text}</h3>
          );
        }
        if (block.kind === 'paragraph') return <p key={i}>{inlines(block.inlines, link)}</p>;
        if (block.kind === 'list')
          return (
            <ul key={i}>
              {block.items.map((item, j) => (
                <li key={j}>{inlines(item, link)}</li>
              ))}
            </ul>
          );
        return (
          <div key={i} className="rd-legal__table">
            <table>
              <thead>
                <tr>
                  {block.head.map((cell, j) => (
                    <th key={j} scope="col">
                      {inlines(cell, link)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, j) => (
                  <tr key={j}>
                    {row.map((cell, k) => (
                      <td key={k}>{inlines(cell, link)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </article>
  );
}

function inlines(nodes: Inline[], link: (href: string, children: ReactNode, i: number) => ReactNode): ReactNode {
  return nodes.map((node, i) => {
    if (node.kind === 'text') return <Fragment key={i}>{node.text}</Fragment>;
    if (node.kind === 'strong') return <strong key={i}>{inlines(node.children, link)}</strong>;
    return link(node.href, inlines(node.children, link), i);
  });
}

/** O e-mail abre o programa de e-mail, o site de fora abre em nova aba, e o outro texto fica aqui. */
function LegalLink({ href, onNavigate, children }: { href: string; onNavigate?: ((doc: LegalDoc) => void) | undefined; children: ReactNode }) {
  const route = legalRoute(href);
  if (route)
    return (
      <a
        href={href}
        onClick={(event) => {
          if (!onNavigate) return;
          event.preventDefault();
          onNavigate(route.doc);
        }}
      >
        {children}
      </a>
    );
  if (/^https?:/.test(href))
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  return <a href={href}>{children}</a>;
}

/** O sumário rola até a seção sem pôr `#` no endereço. */
function jump(id: string) {
  return (event: MouseEvent) => {
    event.preventDefault();
    const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    document.getElementById(id)?.scrollIntoView({ behavior, block: 'start' });
  };
}
